'use client';

import { useState } from 'react';
import { useTranslations } from 'next-intl';
import { HStack, VStack } from '@terra/ui-styled-system';
import { Badge } from '../../../../../components/badge';
import { Text } from '../../../../../components/styled/text';
import { Button } from '../../../../../components/button';
import type { ParsedAdGraphOutput } from './parse-ad-graph-output.util';
import { AdGraphChatFlow } from './ad-graph-chat-flow';

export function AdGraphListBody({ graph }: { graph: ParsedAdGraphOutput }) {
  const t = useTranslations('designSystem.chat.adToolOutput');
  const [view, setView] = useState<'graph' | 'list'>(
    graph.nodes.length > 0 && graph.edges.length > 0 ? 'graph' : 'list',
  );

  const graphSummary = graph.mode
    ? `${t('graphMode', { mode: graph.mode })}${graph.truncated ? t('graphTruncated') : ''}${
        graph.totalCount !== undefined
          ? t('graphNodeCountWithTotal', {
              nodeCount: graph.nodeCount,
              totalCount: graph.totalCount,
            })
          : t('graphNodeCount', { nodeCount: graph.nodeCount })
      }`
    : null;

  const canShowGraph = graph.nodes.length > 0 && graph.edges.length > 0;

  return (
    <VStack alignItems="stretch" gap={2} width="100%">
      <HStack justifyContent="space-between" alignItems="center" gap={2}>
        {graphSummary ? (
          <Text fontSize="2xs" color="fg.muted">
            {graphSummary}
          </Text>
        ) : (
          <span />
        )}
        {canShowGraph ? (
          <HStack gap={1}>
            <Button
              size="xs"
              variant={view === 'graph' ? 'solid' : 'ghost'}
              onClick={() => setView('graph')}
            >
              {t('viewGraph')}
            </Button>
            <Button
              size="xs"
              variant={view === 'list' ? 'solid' : 'ghost'}
              onClick={() => setView('list')}
            >
              {t('viewList')}
            </Button>
          </HStack>
        ) : null}
      </HStack>
      {view === 'graph' && canShowGraph ? (
        <AdGraphChatFlow graph={graph} />
      ) : (
        <>
          <VStack alignItems="stretch" gap={1} width="100%">
            {graph.nodes.map((node) => (
              <HStack key={node.id} gap={1.5} alignItems="center">
                <Badge size="sm" variant="emphasis">
                  {node.kind}
                </Badge>
                <Text fontSize="xs" color="fg.default" truncate flex={1}>
                  {node.label}
                </Text>
                <Text
                  fontSize="2xs"
                  color="fg.muted"
                  fontFamily="mono"
                  truncate
                >
                  {node.id}
                </Text>
              </HStack>
            ))}
          </VStack>
          {graph.edges.length > 0 ? (
            <VStack alignItems="stretch" gap={1} width="100%">
              <Text fontSize="2xs" color="fg.muted">
                {t('edgeCount', { count: graph.edgeCount })}
              </Text>
              {graph.edges.map((edge, index) => (
                <Text
                  key={`${edge.sourceId}-${edge.relationshipType}-${edge.targetId}-${index}`}
                  fontSize="2xs"
                  color="fg.muted"
                  fontFamily="mono"
                >
                  {`${edge.sourceId} -[${edge.relationshipType}]-> ${edge.targetId}`}
                </Text>
              ))}
            </VStack>
          ) : null}
        </>
      )}
    </VStack>
  );
}
